import React, { useState, useEffect } from "react";
import { Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { ROLES } from "../constant/roles";
import { useJupyterWorkspace } from "../contexts/JupyterWorkspaceContext";
import { Button, buttonVariants } from "./ui/button";
import { RequestExtensionDialog } from "./RequestExtensionDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { cn } from "../lib/utils";

const Timer: React.FC<{ isDarkMode?: boolean }> = ({ isDarkMode = false }) => {
  const { workspace, updateWorkspace, resetWorkspace } = useJupyterWorkspace();

  // On page reload the workspace context starts as initialWorkspaceState (remainingTime=null).
  // Read the last-known remaining time directly from sessionStorage so the timer shows a
  // meaningful value immediately while the async validation in JupyterWorkspaceContext runs.
  const [liveRemainingTime, setLiveRemainingTime] = useState<number | null>(() => {
    if (workspace?.remainingTime != null) return workspace.remainingTime;
    try {
      const stored = sessionStorage.getItem("jupyterWorkspaceState");
      if (stored) {
        const parsed = JSON.parse(stored);
        return (typeof parsed?.remainingTime === "number") ? parsed.remainingTime : null;
      }
    } catch {}
    return null;
  });

  // Sync liveRemainingTime with workspace.remainingTime when context updates
  useEffect(() => {
    if (workspace?.remainingTime !== undefined && workspace?.remainingTime !== null) {
      setLiveRemainingTime(workspace.remainingTime);
    }
  }, [workspace?.remainingTime]);

  // When the workspace is fully reset (validation failed / session expired), clear the timer
  useEffect(() => {
    if (workspace?.status === "none" && workspace?.sessionId === null) {
      setLiveRemainingTime(null);
    }
  }, [workspace?.status, workspace?.sessionId]);

  useEffect(() => {
    if (workspace?.status === "error" || workspace?.status === "stopping") {
      setLiveRemainingTime(null);
    }
  }, [workspace?.status]);

  const { user } = useAuth();
  const isStudent = user?.role === ROLES.STUDENT;
  
  if (!isStudent) {
    return null;
  }

  const [timeAlert, setTimeAlert] = useState<{
    open: boolean;
    minutes: number;
  }>({
    open: false,
    minutes: 0,
  });
  const requested = !!workspace?.extension_requested;
  const isPracticeProject = !!workspace?.isPracticeProject;

  const fmt = (totalSecs?: number | null) => {
    if (totalSecs == null || totalSecs <= 0) return "00:00:00";
    const secs = Math.floor(totalSecs);

    const hours = Math.floor(secs / 3600);
    const minutes = Math.floor((secs % 3600) / 60);
    const seconds = secs % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  };

  // The backend heartbeat lifecycle has been moved to JupyterWorkspaceContext.tsx
  // so that sessions stay active regardless of whether this UI component is mounted.

  useEffect(() => {
    if (!isStudent) return;
    const timerId = setInterval(() => {
      setLiveRemainingTime((prevTime) => {
        // If time is null, we are still initializing. Don't start counting down yet
        // and don't default to 0 to avoid the "00:00:00" flicker.
        if (prevTime === null) return null;
        
        const newTime = Math.max(0, prevTime - 1);
        if (!requested && !isPracticeProject) {
          if (newTime === 1800) {
            setTimeAlert({ open: true, minutes: 30 });
          } else if (newTime === 900) {
            setTimeAlert({ open: true, minutes: 15 });
          } else if (newTime === 300) {
            setTimeAlert({ open: true, minutes: 5 });
          }
        }
        return newTime;
      });
    }, 1000);
    return () => clearInterval(timerId);
  }, [liveRemainingTime, requested, isPracticeProject, isStudent]);

  const [extOpen, setExtOpen] = useState(false);
  const onClickRequest = () => {
    if (!requested && !isPracticeProject) setExtOpen(true);
  };

  const handleExtensionRequested = () => {
    updateWorkspace({ extension_requested: true });
    setExtOpen(false);
  };

  const under30Min = liveRemainingTime != null && liveRemainingTime <= 1800;

  if (!isStudent) {
    return null;
  }

  // Determine if we are in an "initializing" state
  const isSessionEnded =
    workspace.status === "error" || workspace.status === "stopping";
  const isInitializing =
    !isSessionEnded &&
    workspace.status === "active" &&
    liveRemainingTime === null;

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Timer Display */}
        <div
          className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-sm transition-all duration-200 border",
            under30Min && !isPracticeProject
              ? "bg-red-50 border-red-300 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400"
              : isSessionEnded
                ? "bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200"
                : "bg-white border-gray-300 text-[#039855] dark:bg-[#161b22] dark:border-[#30363d] dark:text-green-400",
            isInitializing && "animate-pulse opacity-80"
          )}
        >
          <Clock
            className={cn(
              "h-4 w-4",
              under30Min && !isPracticeProject ? "text-red-600 dark:text-red-400" : "text-[#039855] dark:text-green-400"
            )}
          />
          <span className="font-mono text-sm font-semibold min-w-[65px]">
            {isSessionEnded
              ? "Session ended"
              : isInitializing
                ? "Initializing..."
                : fmt(liveRemainingTime)}
          </span>
        </div>

        {/* Request More Time Button */}
        {under30Min && !isPracticeProject && (
          <Button
            variant="outline"
            size="sm"
            className="border-2 border-orange-300 text-orange-700 hover:bg-orange-50 transition-all duration-200 dark:border-orange-800 dark:text-[#f0883e] dark:hover:bg-[#21262d]"
            onClick={onClickRequest}
            disabled={requested}
            title={requested ? "Extension already requested" : "Request more time"}
          >
            <AlertTriangle className="h-4 w-4 mr-1" />
            {requested ? "Extension Requested" : "Request Time"}
          </Button>
        )}
      </div>

      {!isPracticeProject && workspace.sessionId && workspace.status !== "error" && workspace.status !== "stopping" && (
        <RequestExtensionDialog
          open={extOpen}
          onClose={() => setExtOpen(false)}
          sessionId={workspace.sessionId!}
          onRequested={handleExtensionRequested}
          isDarkMode={isDarkMode}
        />
      )}

      <AlertDialog
        open={timeAlert.open}
        onOpenChange={(open) => setTimeAlert({ ...timeAlert, open })}
      >
        <AlertDialogContent className="dark:bg-[#161b22] dark:border-[#30363d]">
          <AlertDialogHeader>
            <AlertDialogTitle className="dark:text-[#f0f6fc]">⏰ Time Warning</AlertDialogTitle>
            <AlertDialogDescription className="dark:text-[#8b949e]">
              Your sandbox session has {timeAlert.minutes} minutes remaining. If
              you need extra time, please request a time extension.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: "outline" }), "dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]")}
            >
              Understood
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Timer;
